import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine,
  AreaChart, Area,
} from "recharts";
import {
  Users, UserCheck, UserX, Clock, AlarmClock, RefreshCw,
  CheckCircle, XCircle, ChevronLeft, ChevronRight, Download,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getAllAttendance } from "../Api/attendanceApi";
import { getAllEmployees } from "../Api/adminApi";
import { getDashboardApi } from "../Api/dashboardApi";

/* ─── colors ───────────────────────────────────── */
const COLORS = {
  total: "#6366f1",
  present: "#0ea5e9",
  onTime: "#10b981",
  late: "#f59e0b",
  absent: "#ef4444",
};

/* ─── helpers ──────────────────────────────────── */
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
// local-date ISO (yyyy-mm-dd) so the "day" matches the admin's own clock
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const shiftDay = (iso, delta) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toISO(d);
};
const fmtTime = (t) =>
  t ? new Date(t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "--";
// On time = checked in at or before 10:30 AM (IST); anything after is late
const ON_TIME_LIMIT_SEC = (10 * 60 + 30) * 60;
const secondsIST = (t) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "Asia/Kolkata",
  }).formatToParts(new Date(t));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return (get("hour") % 24) * 3600 + get("minute") * 60 + get("second");
};
// strictly after 10:30:00 counts as late (10:30:01 → late)
const isLate = (rec) => !!rec?.checkIn?.time && secondsIST(rec.checkIn.time) > ON_TIME_LIMIT_SEC;
const lateBy = (rec) => {
  if (!isLate(rec)) return "--";
  const m = Math.ceil((secondsIST(rec.checkIn.time) - ON_TIME_LIMIT_SEC) / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
};
const attDate = (rec) => (rec?.date ? String(rec.date).slice(0, 10) : "");

/* ─── small building blocks ────────────────────── */
const Panel = ({ title, sub, right, children, className = "" }) => (
  <div className={`bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(15,23,42,0.05)] p-6 ${className}`}>
    <div className="flex items-start justify-between mb-5">
      <div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Kpi = ({ icon: Icon, label, value, share, accent }) => (
  <div className="rounded-2xl p-4 bg-white/[0.07] border border-white/10 backdrop-blur">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-300">{label}</span>
      <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}33` }}>
        <Icon size={16} color={accent} />
      </span>
    </div>
    <p className="text-4xl font-extrabold text-white mt-3 leading-none">{value}</p>
    <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${share}%`, background: accent }} />
    </div>
    <p className="text-[11px] text-slate-400 mt-1.5">{share}% of team</p>
  </div>
);

const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-xl bg-slate-900 text-white text-xs px-3 py-2 shadow-xl">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          {p.name}: <b>{p.value}</b>
        </p>
      ))}
    </div>
  ) : null;

/* ═══════════════ MAIN DASHBOARD ═══════════════ */
const Dashboard = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]); // active employees only
  const [leaves, setLeaves] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [date, setDate] = useState(toISO(new Date()));
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const today = toISO(new Date());

  const load = useCallback(async () => {
    try {
      setError("");
      const [attRes, empRes, dashRes] = await Promise.all([
        getAllAttendance(),
        getAllEmployees(),
        getDashboardApi().catch(() => null),
      ]);
      if (attRes?.success) setAttendance(attRes.data || []);
      if (empRes?.success) {
        // inactive employees are never counted
        setEmployees((empRes.data || []).filter((e) => e.role === "user" && e.isActive));
      }
      if (dashRes?.success) setLeaves(dashRes.data?.leaves || null);
    } catch (e) {
      setError("Could not load dashboard data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    setRefreshing(true);
    load();
  };

  /* stats for a given day, restricted to active employees */
  const statsFor = useCallback(
    (day) => {
      const active = new Map(employees.map((e) => [String(e._id), e]));
      const seen = new Set();
      const checkedIn = [];
      attendance.forEach((rec) => {
        if (attDate(rec) !== day || !rec.checkIn?.time) return;
        const uid = String(rec.user?._id ?? rec.user ?? "");
        if (!active.has(uid) || seen.has(uid)) return; // skip inactive / duplicates
        seen.add(uid);
        checkedIn.push(rec);
      });
      const late = checkedIn.filter(isLate);
      const onTime = checkedIn.filter((r) => !isLate(r));
      const absent = employees.filter((e) => !seen.has(String(e._id)));
      return { total: employees.length, present: checkedIn.length, late, onTime, absent, checkedIn };
    },
    [attendance, employees]
  );

  const day = useMemo(() => statsFor(date), [statsFor, date]);

  /* 7-day attendance-rate trend */
  const trend = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = shiftDay(date, i - 6);
        const s = statsFor(d);
        return {
          label: new Date(`${d}T00:00:00`).toLocaleDateString([], { weekday: "short" }),
          Attendance: pct(s.present, s.total),
          "On Time": s.onTime.length,
          Late: s.late.length,
        };
      }),
    [statsFor, date]
  );

  /* arrival histogram: 30-min buckets, 7:00 → 12:30, split on-time / late */
  const arrivals = useMemo(() => {
    const START = 7 * 60, END = 12 * 60 + 30, STEP = 30;
    const buckets = [];
    for (let m = START; m <= END; m += STEP) {
      const h = Math.floor(m / 60), mm = m % 60;
      buckets.push({ m, label: `${h > 12 ? h - 12 : h}:${String(mm).padStart(2, "0")}`, "On Time": 0, Late: 0 });
    }
    day.checkedIn.forEach((r) => {
      const mins = Math.floor(secondsIST(r.checkIn.time) / 60);
      const idx = Math.min(buckets.length - 1, Math.max(0, Math.floor((mins - START) / STEP)));
      buckets[idx][isLate(r) ? "Late" : "On Time"] += 1;
    });
    return buckets;
  }, [day]);

  const rate = pct(day.present, day.total);
  const onTimeRate = pct(day.onTime.length, day.present);
  const isToday = date === today;
  const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString([], {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const ringData = [
    { name: "Present", value: rate, fill: COLORS.present },
    { name: "On Time", value: pct(day.onTime.length, day.total), fill: COLORS.onTime },
    { name: "Late", value: pct(day.late.length, day.total), fill: COLORS.late },
  ];

  const hoursOf = (r) => {
    if (!r?.checkIn?.time || !r?.checkOut?.time) return "--";
    const ms = new Date(r.checkOut.time) - new Date(r.checkIn.time);
    if (ms <= 0) return "--";
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  };

  const locOf = (l) =>
    l?.latitude && l?.longitude
      ? {
          coords: `${l.latitude}, ${l.longitude}`,
          link: `https://www.google.com/maps?q=${l.latitude},${l.longitude}`,
          address: l.address || l.name || "",
        }
      : null;

  /* every active employee with their status for the selected day */
  const allRows = useMemo(() => {
    const rec = new Map(day.checkedIn.map((r) => [String(r.user?._id ?? r.user), r]));
    return employees
      .map((e) => {
        const r = rec.get(String(e._id));
        return {
          id: e._id,
          name: e.name || "Unknown",
          status: !r ? "Absent" : isLate(r) ? "Late" : "On Time",
          inAt: r ? new Date(r.checkIn.time).getTime() : Infinity,
          checkIn: r ? fmtTime(r.checkIn.time) : "--",
          checkOut: r?.checkOut?.time ? fmtTime(r.checkOut.time) : "--",
          hours: hoursOf(r),
          lateBy: lateBy(r),
          inLoc: locOf(r?.checkIn?.location),
          outLoc: locOf(r?.checkOut?.location),
        };
      })
      .sort((a, b) => a.inAt - b.inAt || a.name.localeCompare(b.name));
  }, [day, employees]);

  const tabRows = allRows.filter((r) => {
    if (tab === "present" && r.status === "Absent") return false;
    if (tab === "late" && r.status !== "Late") return false;
    if (tab === "absent" && r.status !== "Absent") return false;
    return r.name.toLowerCase().includes(query.trim().toLowerCase());
  });
  const TABS = [
    { key: "all", label: "All", n: allRows.length },
    { key: "present", label: "Present", n: day.present },
    { key: "late", label: "Late", n: day.late.length },
    { key: "absent", label: "Absent", n: day.absent.length },
  ];
  const STATUS_STYLE = {
    "On Time": "bg-emerald-50 text-emerald-700",
    Late: "bg-amber-50 text-amber-700",
    Absent: "bg-red-50 text-red-700",
  };

  /* ── Excel daily report for the selected date ── */
  /* place names are resolved in the background (cached), so the download itself is instant */
  const [placeNames, setPlaceNames] = useState(() => {
    try { return JSON.parse(localStorage.getItem("placeNames") || "{}"); } catch { return {}; }
  });
  const keyOf = (l) => {
    const [lat, lng] = l.coords.split(", ");
    return `${(+lat).toFixed(4)},${(+lng).toFixed(4)}`;
  };

  const lookupName = async (k) => {
    const [lat, lng] = k.split(",");
    try {
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      );
      const d = await res.json();
      return [d.locality, d.city, d.principalSubdivision, d.countryName]
        .filter((v, i, arr) => v && arr.indexOf(v) === i)
        .join(", ");
    } catch {
      return "";
    }
  };

  /* resolve all missing place names in parallel batches; returns the full name map */
  const resolveNames = async () => {
    const keys = [];
    for (const r of allRows)
      for (const l of [r.inLoc, r.outLoc])
        if (l && !l.address && !placeNames[keyOf(l)] && !keys.includes(keyOf(l))) keys.push(keyOf(l));
    const found = {};
    for (let i = 0; i < keys.length; i += 10) {
      await Promise.all(
        keys.slice(i, i + 10).map(async (k) => {
          const n = await lookupName(k);
          if (n) found[k] = n;
        })
      );
    }
    if (!Object.keys(found).length) return placeNames;
    const next = { ...placeNames, ...found };
    setPlaceNames(next);
    try { localStorage.setItem("placeNames", JSON.stringify(next)); } catch {}
    return next;
  };

  // warm the cache in the background so the download is instant
  useEffect(() => {
    resolveNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows]);

  const downloadReport = async () => {
    const names = await Promise.race([
      resolveNames(),
      new Promise((ok) => setTimeout(() => ok(placeNames), 8000)),
    ]);
    const place = (l) => (l ? l.address || names[keyOf(l)] || "Name unavailable (see map link)" : "--");
    const long = new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
    const summary = [
      ["Daily Attendance Report"],
      ["Date", long],
      ["On-time cut-off", "10:30 AM"],
      [],
      ["Metric", "Count", "% of team"],
      ["Total Employees", day.total, "100%"],
      ["Present", day.present, `${pct(day.present, day.total)}%`],
      ["On Time", day.onTime.length, `${pct(day.onTime.length, day.total)}%`],
      ["Late", day.late.length, `${pct(day.late.length, day.total)}%`],
      ["Absent", day.absent.length, `${pct(day.absent.length, day.total)}%`],
    ];

    const toRow = (r, i) => ({
      "S.No": i + 1,
      Name: r.name,
      Date: date,
      Status: r.status,
      "Check In": r.checkIn,
      "Check Out": r.checkOut,
      "Hours Worked": r.hours,
      "Late By": r.lateBy,
      "Check-In Location": place(r.inLoc),
      "Check-In Map Link": r.inLoc ? r.inLoc.link : "--",
      "Check-Out Location": place(r.outLoc),
      "Check-Out Map Link": r.outLoc ? r.outLoc.link : "--",
    });
    const allSheet = allRows.map(toRow);
    const presentRows = allRows.filter((r) => r.status !== "Absent").map(toRow);
    const absentSheetRows = allRows.filter((r) => r.status === "Absent").map(toRow);

    const COLS = [{ wch: 6 }, { wch: 26 }, { wch: 12 }, { wch: 10 }, { wch: 11 }, { wch: 11 }, { wch: 13 }, { wch: 10 }, { wch: 32 }, { wch: 48 }, { wch: 32 }, { wch: 48 }];
    const wb = XLSX.utils.book_new();
    const wsSummary = XLSX.utils.aoa_to_sheet(summary);
    wsSummary["!cols"] = [{ wch: 22 }, { wch: 34 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    const wsAll = XLSX.utils.json_to_sheet(allSheet.length ? allSheet : [{ Name: "No active employees" }]);
    wsAll["!cols"] = COLS;
    XLSX.utils.book_append_sheet(wb, wsAll, "All Employees");

    const wsPresent = XLSX.utils.json_to_sheet(
      presentRows.length ? presentRows : [{ Name: "No check-ins" }]
    );
    wsPresent["!cols"] = COLS;
    XLSX.utils.book_append_sheet(wb, wsPresent, "Present");

    const wsAbsent = XLSX.utils.json_to_sheet(
      absentSheetRows.length ? absentSheetRows : [{ Name: "No absentees" }]
    );
    wsAbsent["!cols"] = COLS;
    XLSX.utils.book_append_sheet(wb, wsAbsent, "Absent");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], { type: "application/octet-stream" }),
      `Attendance_Report_${date}.xlsx`
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* ── HERO ── */}
        <section className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-6 sm:p-8 text-white shadow-2xl">
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-indigo-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-semibold">
                {isToday ? "Live · Today" : "History"}
              </p>
              <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">Attendance Overview</h1>
              <p className="text-slate-300 mt-1 text-sm">{dateLabel}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setDate(shiftDay(date, -1))}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10"
                aria-label="Previous day"
              >
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={date}
                max={today}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="rounded-xl px-3 py-2 text-sm bg-white/10 border border-white/10 text-white [color-scheme:dark]"
              />
              <button
                onClick={() => setDate(shiftDay(date, 1))}
                disabled={isToday}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 disabled:opacity-30"
                aria-label="Next day"
              >
                <ChevronRight size={16} />
              </button>
              {!isToday && (
                <button onClick={() => setDate(today)} className="px-3 py-2.5 rounded-xl text-sm font-semibold bg-cyan-400 text-slate-900">
                  Today
                </button>
              )}
              <button
                onClick={downloadReport}
                disabled={loading || !!error}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-400 text-slate-900 hover:bg-emerald-300 disabled:opacity-40"
              >
                <Download size={14} />
                Download Excel
              </button>
              <button
                onClick={refresh}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-400 to-indigo-500 text-white"
              >
                <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="relative flex justify-center items-center h-60">
              <div className="w-10 h-10 rounded-full border-[3px] border-white/20 border-t-cyan-300 animate-spin" />
            </div>
          ) : error ? (
            <div className="relative mt-6 rounded-xl bg-red-500/20 border border-red-400/30 p-4 text-sm">{error}</div>
          ) : (
            <div className="relative mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
              {/* concentric ring gauge */}
              <div className="lg:col-span-2 relative h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    innerRadius="45%" outerRadius="100%" data={ringData}
                    startAngle={90} endAngle={-270} barSize={14}
                  >
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar dataKey="value" background={{ fill: "rgba(255,255,255,0.08)" }} cornerRadius={10} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-5xl font-extrabold leading-none">{rate}%</span>
                  <span className="text-xs text-slate-300 mt-1 uppercase tracking-widest">Present</span>
                  <span className="text-[11px] text-slate-400 mt-1">{day.present} of {day.total}</span>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="flex flex-wrap gap-3 mb-4 text-xs text-slate-300">
                  {ringData.map((r) => (
                    <span key={r.name} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.fill }} />
                      {r.name}
                    </span>
                  ))}
                  <span className="ml-auto font-semibold text-emerald-300">{onTimeRate}% punctual</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Kpi icon={Users} label="Total Employees" value={day.total} share={100} accent={COLORS.total} />
                  <Kpi icon={UserCheck} label="On Time" value={day.onTime.length} share={pct(day.onTime.length, day.total)} accent={COLORS.onTime} />
                  <Kpi icon={AlarmClock} label="Late" value={day.late.length} share={pct(day.late.length, day.total)} accent={COLORS.late} />
                  <Kpi icon={UserX} label="Absent" value={day.absent.length} share={pct(day.absent.length, day.total)} accent={COLORS.absent} />
                </div>
              </div>
            </div>
          )}
        </section>

        {!loading && !error && (
          <>
            {/* ── CHARTS ── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <Panel
                className="lg:col-span-3"
                title="Arrival Timeline"
                sub="When people checked in · cut-off 10:30 AM"
                right={
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">
                    {day.onTime.length} on time
                  </span>
                }
              >
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={arrivals} margin={{ top: 10, right: 8, left: -24, bottom: 0 }} barCategoryGap={4}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={1} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: "#f1f5f9" }} />
                      <ReferenceLine x="10:30" stroke="#ef4444" strokeDasharray="4 4" label={{ value: "Cut-off", position: "top", fontSize: 10, fill: "#ef4444" }} />
                      <Bar dataKey="On Time" stackId="a" fill={COLORS.onTime} radius={[0, 0, 4, 4]} />
                      <Bar dataKey="Late" stackId="a" fill={COLORS.late} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel className="lg:col-span-2" title="7-Day Attendance" sub="Daily attendance rate (%)">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trend} margin={{ top: 10, right: 8, left: -24, bottom: 0 }}>
                      <defs>
                        <linearGradient id="rateFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.total} stopOpacity={0.45} />
                          <stop offset="100%" stopColor={COLORS.total} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} />
                      <Area
                        type="monotone" dataKey="Attendance" stroke={COLORS.total} strokeWidth={3}
                        fill="url(#rateFill)" dot={{ r: 4, fill: "#fff", stroke: COLORS.total, strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            {/* ── EMPLOYEE TABLE ── */}
            <Panel
              title="All Active Employees"
              sub={`${allRows.length} employees · attendance for ${dateLabel}`}
              right={
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name…"
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm w-40 sm:w-56 focus:outline-none focus:border-indigo-300"
                />
              }
            >
              <div className="flex flex-wrap gap-2 mb-4">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                      tab === t.key ? "bg-slate-900 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t.label} <span className="opacity-70">({t.n})</span>
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-100 max-h-[28rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-3 w-12">#</th>
                      <th className="text-left px-4 py-3">Employee</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Check In</th>
                      <th className="text-left px-4 py-3">Check Out</th>
                      <th className="text-left px-4 py-3">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tabRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-400">
                          No employees to show
                        </td>
                      </tr>
                    ) : (
                      tabRows.map((r, i) => (
                        <tr key={r.id} className={r.status === "Absent" ? "bg-red-50/40" : "hover:bg-slate-50"}>
                          <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                          <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status]}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">{r.checkIn}</td>
                          <td className="px-4 py-3 text-slate-700">{r.checkOut}</td>
                          <td className="px-4 py-3 text-slate-500">{r.hours}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>

            {/* ── LEAVES ── */}
            {leaves && (
              <Panel title="Leave Requests" sub="Current status across the team">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Pending", value: leaves.pending, icon: Clock, col: "#f59e0b" },
                    { label: "Approved", value: leaves.approved, icon: CheckCircle, col: "#10b981" },
                    { label: "Rejected", value: leaves.rejected, icon: XCircle, col: "#ef4444" },
                  ].map(({ label, value, icon: Icon, col }) => (
                    <div key={label} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: `${col}12` }}>
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-white shadow-sm">
                        <Icon size={18} color={col} />
                      </span>
                      <div>
                        <p className="text-2xl font-extrabold text-slate-900 leading-none">{value ?? 0}</p>
                        <p className="text-xs text-slate-500 mt-1">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
